import React, { useState, useCallback, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Cropper from 'react-easy-crop';
import { 
    PhotoCamera, Save, ArrowForward, Person, Numbers, 
    CalendarToday, VerifiedUser, School, Close, AddAPhoto 
} from '@mui/icons-material';
import { snackbar } from '../utils/showMessage';
import { 
    Autocomplete, TextField, Box, InputAdornment, Container, 
    Typography, Paper, Avatar, IconButton, Button, Dialog, 
    DialogTitle, DialogContent, DialogActions, Slider, useTheme, useMediaQuery,
    Stack
} from '@mui/material';
import { normalizeDigits } from '../utils/normalizeDigits';

const formatToJalaliDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmed)) {
            return trimmed;
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return typeof value === 'string' ? value : '';
    }

    try {
        return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(date).replace(/\u200f/g, '');
    } catch (e) {
        return typeof value === 'string' ? value : '';
    }
};

const jalaliToGregorian = (jy, jm, jd) => {
    let year = parseInt(jy, 10);
    let month = parseInt(jm, 10);
    let day = parseInt(jd, 10);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
        return null;
    }

    let gy;
    if (year > 979) {
        gy = 1600;
        year -= 979;
    } else {
        gy = 621;
    }

    let days = (365 * year) + Math.floor(year / 33) * 8 + Math.floor(((year % 33) + 3) / 4) + 78 + day;
    days += month < 7 ? (month - 1) * 31 : ((month - 7) * 30) + 186;

    gy += 400 * Math.floor(days / 146097);
    days %= 146097;

    if (days > 36524) {
        gy += 100 * Math.floor(--days / 36524);
        days %= 36524;
        if (days >= 365) {
            days += 1;
        }
    }

    gy += 4 * Math.floor(days / 1461);
    days %= 1461;

    if (days > 365) {
        gy += Math.floor((days - 1) / 365);
        days = (days - 1) % 365;
    }

    let gd = days + 1;
    const leap = (gy % 4 === 0 && gy % 100 !== 0) || (gy % 400 === 0);
    const sal_a = [0, 31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let gm = 0;
    for (gm = 1; gm <= 12 && gd > sal_a[gm]; gm += 1) {
        gd -= sal_a[gm];
    }

    return [gy, gm, gd];
};

const toGregorianDateString = (jalaliInput) => {
    if (!jalaliInput) return '';
    const normalized = normalizeDigits(String(jalaliInput).trim());
    const match = normalized.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
    if (!match) return normalized;
    const result = jalaliToGregorian(match[1], match[2], match[3]);
    if (!result) return normalized;
    const [gy, gm, gd] = result;
    const pad = (val) => String(val).padStart(2, '0');
    return `${gy}-${pad(gm)}-${pad(gd)}`;
};

const Profile = () => {
    const { user, updateUser, refreshUser } = useUser();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [image, setImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [specialitiesList, setSpecialitiesList] = useState([]);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        medicalSystemNumber: '',
        nationalityCode: '',
        birthdate: '',
        specialities: [],
    });

    const textFieldStyle = {
        mb: 2.5,
        '& .MuiOutlinedInput-root': {
            borderRadius: '15px',
            backgroundColor: '#fff',
            '& fieldset': {
                borderColor: '#eee',
            },
            '&:hover fieldset': {
                borderColor: '#ccc',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: '14px',
        }
    };

    useEffect(() => {
        fetchSpecialities();
    }, []);

    useEffect(() => {
        const loadUser = async () => {
            try {
                await refreshUser();
            } catch (e) {
                console.error('Failed to refresh user', e);
            }
        };
        loadUser();
    }, [refreshUser]);

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                medicalSystemNumber: user.medicalSystemNumber || '',
                nationalityCode: user.nationalityCode || '',
                birthdate: formatToJalaliDate(user.birthdate || ''),
                specialities: (user.specialities || []).map(s => s._id || s),
            });
        }
    }, [user]);

    const fetchSpecialities = async () => {
        try {
            const response = await api.get('/specialities');
            setSpecialitiesList(response.data);
        } catch (error) {
            console.error('Error fetching specialities:', error);
        }
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const onFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => setImage(reader.result));
            reader.readAsDataURL(e.target.files[0]);
            setShowCropper(true);
        }
    };

    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.setAttribute('crossOrigin', 'anonymous');
            image.src = url;
        });

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg');
        });
    };

    const handleUpload = async () => {
        try {
            setLoading(true);
            const croppedImage = await getCroppedImg(image, croppedAreaPixels);
            const formDataUpload = new FormData();
            formDataUpload.append('file', croppedImage, 'profile.jpg');

            const res = await api.post('/users/profile-image', formDataUpload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            });

            updateUser({ profileImage: res.data.filename });
            setShowCropper(false);
            setImage(null);
        } catch (e) {
            console.error(e);
            snackbar('خطا در آپلود عکس');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                birthdate: toGregorianDateString(formData.birthdate),
            };
            await api.put('/users/profile', payload);
            updateUser(formData);
            snackbar('پروفایل با موفقیت بروزرسانی شد');
        } catch (err) {
            console.error(err);
            snackbar('خطا در بروزرسانی پروفایل');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-container container pb-5">
            <div className="row mb-4">
                <div className="col-12 text-center mt-4">
                    <div className="position-relative d-inline-block">
                        <figure className="avatar avatar-120 rounded-circle shadow-sm mb-3">
                            <img src={user?.profileImage ? `${process.env.REACT_APP_FILE_BASE_URL}/uploads/profile/${user.profileImage}` : "/assets/img/user1.jpg"} alt="" className="rounded-circle" />
                        </figure>
                        <label htmlFor="file-input" className="btn btn-sm btn-default btn-rounded position-absolute bottom-0 end-0 mb-3 me-2 shadow">
                            <PhotoCamera sx={{ fontSize: 18 }} />
                        </label>
                        <input id="file-input" type="file" className="d-none" onChange={onFileChange} accept="image/*" />
                    </div>
                    <h4 className="mb-0">{user?.firstName} {user?.lastName}</h4>
                    <p className="text-secondary">{user?.medicalSystemNumber ? `شماره نظام: ${user.medicalSystemNumber}` : 'عضو سازمان'}</p>
                </div>
            </div>

            {showCropper && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content bg-dark border-0">
                            <div className="modal-body p-0 position-relative" style={{ height: '400px' }}>
                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                />
                            </div>
                            <div className="modal-footer border-0 p-3 flex-column">
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    aria-labelledby="Zoom"
                                    onChange={(e) => setZoom(e.target.value)}
                                    className="form-range mb-3"
                                />
                                <div className="w-100 d-flex gap-2">
                                    <button className="btn btn-lg btn-default w-100" onClick={handleUpload} disabled={loading}>
                                        {loading ? 'در حال آپلود...' : 'تایید و آپلود'}
                                    </button>
                                    <button className="btn btn-lg btn-light w-100" onClick={() => setShowCropper(false)}>انصراف</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-12 col-md-12  mx-auto">
                    <div className="card shadow-sm border-0 mb-4 h-100">
                        <div className="card-body">
                            <form onSubmit={handleSaveProfile}>
                                <TextField
                                    fullWidth
                                    label="نام"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    sx={textFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="نام خانوادگی"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    sx={textFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="شماره نظام پزشکی"
                                    value={formData.medicalSystemNumber}
                                    onChange={(e) => setFormData({ ...formData, medicalSystemNumber: e.target.value })}
                                    sx={textFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Numbers color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="کد ملی"
                                    value={formData.nationalityCode}
                                    onChange={(e) => setFormData({ ...formData, nationalityCode: e.target.value })}
                                    sx={textFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VerifiedUser color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Autocomplete
                                    multiple
                                    options={specialitiesList}
                                    getOptionLabel={(option) => `${option.title} (${option.level})`}
                                    value={specialitiesList.filter(s => formData.specialities.includes(s._id))}
                                    onChange={(event, newValue) => {
                                        setFormData({ ...formData, specialities: newValue.map(v => v._id) });
                                    }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="تخصص‌ها"
                                            placeholder="جستجو و انتخاب..."
                                            sx={textFieldStyle}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start" sx={{ ml: 1 }}>
                                                            <School color="action" />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    sx={{ width: '100%' }}
                                />

                                <TextField
                                    fullWidth
                                    name="birthdate"
                                    label="تاریخ تولد"
                                    value={formData.birthdate}
                                    onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                                    placeholder="1370/01/01"
                                    sx={textFieldStyle}
                                />
                                <button type="submit" className="btn btn-lg btn-default w-100 shadow mt-3" disabled={loading}>
                                    <Save sx={{ fontSize: 20, verticalAlign: 'middle' }} className="me-2" /> {loading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
